import { useEffect, useRef, useState } from "react";
import { uid, type Field } from "@proof/core";
import { AssetImage, type AssetInfo } from "@proof/ui";
import { useAuthoringServices } from "./services";
import { Button, Control } from "./components";

export function ImageChoices({
  field,
  onChange,
}: {
  field: Field;
  onChange: (patch: Partial<Field>) => void;
}) {
  const services = useAuthoringServices(),
    latest = useRef(field),
    apply = useRef(onChange),
    controller = useRef<AbortController | undefined>(undefined);
  latest.current = field;
  apply.current = onChange;
  const [assets, setAssets] = useState<AssetInfo[]>(services.assets || []),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [selected, select] = useState(""),
    [filter, setFilter] = useState(""),
    [revision, refresh] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    if (services.assets) setAssets(services.assets);
    if (services.listAssets && field.source.kind !== "dataset")
      void services
        .listAssets(abort.signal)
        .then((items) => {
          if (!abort.signal.aborted) {
            setAssets(items);
            setError("");
          }
        })
        .catch((err) => {
          if (!abort.signal.aborted) setError((err as Error).message);
        });
    return () => abort.abort();
  }, [services.listAssets, services.assets, revision, field.source.kind]);
  useEffect(() => () => controller.current?.abort(), []);
  const assign = (value: string, assetId: string) => {
    if (
      latest.current.source.kind === "dataset" ||
      !latest.current.choices.some((choice) => choice.value === value)
    )
      return;
    apply.current({
      choices: latest.current.choices.map((choice) => {
        if (choice.value !== value) return choice;
        const { assetId: previous, ...rest } = choice;
        return { ...rest, ...(assetId ? { assetId } : {}) };
      }),
    });
  };
  const upload = async (file: File, value: string) => {
    if (!services.upload || busy) return;
    const abort = new AbortController();
    const source = JSON.stringify(latest.current.source);
    controller.current = abort;
    setBusy(true);
    setError("");
    try {
      const asset = await services.upload(file, latest.current, abort.signal);
      if (abort.signal.aborted) return;
      setAssets((current) => [
        ...current.filter((a) => a.id !== asset.id),
        asset,
      ]);
      if (source === JSON.stringify(latest.current.source))
        assign(value, asset.id);
    } catch (err) {
      if (!abort.signal.aborted) setError((err as Error).message);
    } finally {
      if (!abort.signal.aborted) setBusy(false);
    }
  };
  const uploadMany = async (files: File[]) => {
    if (!services.upload || busy || !files.length) return;
    const abort = new AbortController();
    controller.current?.abort();
    controller.current = abort;
    const identity = latest.current.id,
      source = JSON.stringify(latest.current.source);
    setBusy(true);
    setError("");
    const choices: Field["choices"] = [];
    try {
      for (const file of files) {
        const asset = await services.upload(file, latest.current, abort.signal);
        if (abort.signal.aborted) return;
        choices.push({
          value: uid("image-choice"),
          label: file.name,
          assetId: asset.id,
        });
      }
      if (
        latest.current.id === identity &&
        JSON.stringify(latest.current.source) === source
      )
        apply.current({ choices: [...latest.current.choices, ...choices] });
    } catch (error) {
      if (!abort.signal.aborted) setError((error as Error).message);
    } finally {
      if (!abort.signal.aborted) setBusy(false);
    }
  };
  if (field.source.kind === "dataset")
    return (
      <p className="muted">
        Image assignments belong to the pinned dataset version. Edit and publish
        that dataset in Rules & presets.
      </p>
    );
  const matchingAssets = assets.filter((asset) =>
    asset.name.toLowerCase().includes(filter.toLowerCase()),
  );
  const choice =
    field.choices.find((c) => c.value === selected) || field.choices[0];
  return (
    <div className="image-choice-assets">
      <Control
        label="Filter asset set"
        note="Search the images available to this project. Adding a set saves its current choices; it does not expose additional library assets."
      >
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter image names…"
        />
      </Control>
      <Button
        disabled={busy || !matchingAssets.length}
        onClick={() => {
          if (
            field.choices.length &&
            !window.confirm(
              `Replace the current options with ${matchingAssets.length} matching images?`,
            )
          )
            return;
          onChange({
            choices: matchingAssets.map((asset) => ({
              value: asset.id,
              label: asset.name,
              assetId: asset.id,
            })),
          });
        }}
      >
        Use matching asset set ({matchingAssets.length})
      </Button>
      {services.upload && (
        <div
          className="panel padded"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            void uploadMany(Array.from(event.dataTransfer.files));
          }}
        >
          <label>
            Drop images here or upload multiple images
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={busy}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                event.target.value = "";
                void uploadMany(files);
              }}
            />
          </label>
        </div>
      )}
      <p className="muted">
        Choose an image for each option. Selecting an option keeps its original
        value in saved proofs.
      </p>
      {services.listAssets && (
        <Button onClick={() => refresh((n) => n + 1)}>
          Refresh image library
        </Button>
      )}
      {error && <p role="alert">{error}</p>}
      <Control label="Image option">
        <select
          value={choice?.value || ""}
          onChange={(e) => select(e.target.value)}
        >
          {field.choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Control>
      {choice && (
        <div key={choice.value}>
          <Control label={`Image for ${choice.label}`}>
            <select
              disabled={busy}
              value={choice.assetId || ""}
              onChange={(e) => assign(choice.value, e.target.value)}
            >
              <option value="">No image assigned</option>
              {choice.assetId &&
                !assets.some((a) => a.id === choice.assetId) && (
                  <option value={choice.assetId}>Current image</option>
                )}
              {matchingAssets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </Control>
          {choice.assetId && (
            <AssetImage
              assetId={choice.assetId}
              assetUrl={services.assetUrl}
              loadAsset={services.loadAsset}
              alt={`${choice.label} option image`}
              style={{ maxWidth: 120, maxHeight: 80 }}
            />
          )}
          {services.upload && (
            <Control label={`Upload image for ${choice.label}`}>
              <input
                disabled={busy}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/tiff"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void upload(file, choice.value);
                }}
              />
            </Control>
          )}
        </div>
      )}
      {busy && <p role="status">Uploading option image…</p>}
    </div>
  );
}
